import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import RecordPosition from "../RecordPosition/RecordPosition";
import { useSimilarBlogs } from "./useHook";

const SimilarBlogsModal = ({ isOpen, onClose, blogId }) => {
  const {
    getSimilarBlogsData,
    addSimilarBlogs,
    deleteSimilarBlog,
    getBlogsListingForSelection,
    updateSimilarBlogPosition,
  } = useSimilarBlogs();

  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedForPosition, setSelectedForPosition] = useState(null);

  const [linkedItems, setLinkedItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [targetType, setTargetType] = useState("blog");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const moduleOptions = [
    { value: "blog", label: "Blog" },
    { value: "house_design", label: "House Design" },
    { value: "shop", label: "Shop" },
    { value: "interior_design", label: "Interior Design" },
    { value: "service", label: "Service" },
    { value: "contract", label: "Contract" },
    { value: "construction_calculator", label: "Construction Calculator" },
    { value: "our_project", label: "Our Project" },
  ];

  useEffect(() => {
    if (!isOpen || !blogId) return;
    fetchLinkedItems();
  }, [isOpen, blogId]);

  useEffect(() => {
    if (!isOpen || !blogId) return;
    
    const delayDebounceFn = setTimeout(() => {
      fetchAvailableItems();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [isOpen, targetType, searchQuery]);

  const fetchLinkedItems = async () => {
    setIsLoading(true);
    try {
      await getSimilarBlogsData(setLinkedItems, blogId);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableItems = async () => {
    setIsLoading(true);
    try {
      await getBlogsListingForSelection(setAvailableItems, blogId, targetType, searchQuery);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  const handleToggleSelection = (item) => {
    const exists = selectedItems.find(
      (i) => i.targetId === item.id && i.targetType === targetType
    );
    if (exists) {
      setSelectedItems((prev) =>
        prev.filter((i) => !(i.targetId === item.id && i.targetType === targetType))
      );
    } else {
      setSelectedItems((prev) => [
        ...prev,
        { targetId: item.id, targetType: targetType, title: item.name || item.title },
      ]);
    }
  };

  const handleRemoveChip = (targetId, type) => {
    setSelectedItems((prev) =>
      prev.filter((i) => !(i.targetId === targetId && i.targetType === type))
    );
  };

  const filteredItems = Array.isArray(availableItems)
    ? availableItems.filter((item) =>
        (item.name || item.title || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const handleSave = async () => {
    if (isSubmitting || selectedItems.length === 0) return;
    setIsSubmitting(true);
    try {
      await addSimilarBlogs(blogId, selectedItems);
      Swal.fire({
        title: "Success",
        text: "Similar items added successfully",
        icon: "success",
      });
      setSelectedItems([]);
      fetchLinkedItems();
    } catch (error) {
      Swal.fire("Error", "Failed to add similar items", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (targetId, type) => {
    if (deletingId) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to remove this similar item link?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    setDeletingId(targetId);
    try {
      await deleteSimilarBlog(blogId, targetId, type);
      Swal.fire("Deleted", "Link removed successfully", "success");
      fetchLinkedItems();
    } catch (error) {
      Swal.fire("Error", "Failed to remove link", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handlePositionClick = (item) => {
    setSelectedForPosition({
      recordId: item.id,
      currentPosition: item.position || 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, position) => {
    await updateSimilarBlogPosition(recordId, position);
    fetchLinkedItems();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-[800px] max-w-[95vw] relative flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Similar Items Management</h2>
            <p className="text-sm text-gray-500">Add related blogs, products, or designs</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl">
            &times;
          </button>
        </div>

        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 rounded-2xl flex items-center justify-center z-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
          </div>
        )}

        {/* Currently Linked Items */}
        <div className="mb-6 flex-1 overflow-y-auto pr-2">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            Linked Similar Items
          </h3>
          <div className="space-y-3">
            {linkedItems.length > 0 ? (
              linkedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <button
                      type="button"
                      onClick={() => handlePositionClick(item)}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg font-bold text-sm hover:bg-blue-200 transition-colors"
                      title="Update Position"
                    >
                      #{item.position || "—"}
                    </button>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-800 truncate max-w-[400px]">
                        {item.targetItem?.name || item.targetItem?.title || "Untitled Item"}
                      </span>
                      <span className="text-xs font-bold text-blue-500 uppercase tracking-wider">
                        {item.targetType?.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(item.targetId, item.targetType)}
                    className="text-red-500 hover:bg-red-100 p-2 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                No similar items linked yet.
              </div>
            )}
          </div>
        </div>

        {/* Selected Chips */}
        <div className="mb-4 flex flex-wrap gap-2 max-h-24 overflow-y-auto">
          {selectedItems.map((item) => (
            <span
              key={`${item.targetType}-${item.targetId}`}
              className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 shadow-sm"
            >
              <span className="bg-blue-200 text-blue-900 px-1.5 rounded mr-2 text-[10px] font-bold uppercase">
                {item.targetType?.replace("_", " ")}
              </span>
              {item.title || "Unknown"}
              <button
                onClick={() => handleRemoveChip(item.targetId, item.targetType)}
                className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
              >
                &times;
              </button>
            </span>
          ))}
        </div>

        {/* Target Module & Search Selection */}
        <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 shadow-inner">
          <div className="flex gap-4 mb-4">
            <div className="w-48">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Target Module</label>
              <select
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value);
                  setIsDropdownOpen(true);
                }}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white font-medium text-sm transition-all"
              >
                {moduleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1" ref={dropdownRef}>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Search Items</label>
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={`Search ${targetType.replace("_", " ")}s...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={() => setIsDropdownOpen(true)}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white font-medium text-sm transition-all"
                />
                <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>

                {isDropdownOpen && (
                  <div className="absolute z-30 top-full mt-2 w-full bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2">
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item) => {
                        const isAlreadyLinked = linkedItems.some(
                          (lb) => String(lb.targetId) === String(item.id) && lb.targetType === targetType
                        );
                        const isSelected = selectedItems.some(
                          (si) => String(si.targetId) === String(item.id) && si.targetType === targetType
                        );
                        return (
                          <div
                            key={item.id}
                            className={`flex items-center px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 ${
                              isAlreadyLinked ? "opacity-60 cursor-default bg-gray-50" : ""
                            }`}
                            onClick={() => !isAlreadyLinked && handleToggleSelection(item)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected || isAlreadyLinked}
                              disabled={isAlreadyLinked}
                              readOnly
                              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-4"
                            />
                            <div className="flex-1">
                              <span className="font-medium text-gray-800">{item.name || item.title}</span>
                              {isAlreadyLinked && (
                                <span className="ml-2 text-[10px] font-bold text-blue-500 uppercase bg-blue-50 px-1.5 py-0.5 rounded">
                                  Added
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-8 text-gray-500 text-center">No items found.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            disabled={isSubmitting || selectedItems.length === 0}
            onClick={handleSave}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-blue-200 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Adding Items...
              </>
            ) : (
              `Add ${selectedItems.length} Selected Item${selectedItems.length !== 1 ? "s" : ""}`
            )}
          </button>
        </div>
      </div>

      {isPositionModalOpen && selectedForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={() => setIsPositionModalOpen(false)}
          currentPosition={selectedForPosition.currentPosition}
          recordId={selectedForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Similar Item"
        />
      )}
    </div>
  );
};

export default SimilarBlogsModal;
